import { Dropdown } from '@fluentui/react';
import * as React from 'react';

import {
  EntityTypePropertySelector,
  EntityTypePropertyValue,
} from '../../../Common/Components/EntityTypePropertySelector';
import { JoinedEntityTypePropertySelector } from '../../../Common/Components/JoinedEntityTypePropertySelector';
import { EntityType } from '../../../Common/DataOwnerModels/CDMRecord';
import { IMachineLearningExperiment } from '../../../Common/MachineLearningModels/IMachineLearningExperiment';
import * as FormStyle from '../../../Common/Styles/form.styled';
import { EntityTypeSelectionPreview } from './EntityTypeSelectionPreview';

export type MachineLearningEntityTypeSelectorProps = {
  disabled?: boolean;
  entityTypes: EntityType[];
  value: IMachineLearningExperiment;
  onChange(value: IMachineLearningExperiment): void;
  readonly?: boolean;
};

export function MachineLearningEntityTypeSelector(props: MachineLearningEntityTypeSelectorProps): React.ReactElement {
  const [joinedProperty, setJoinedProperty] = React.useState<string | null>(null);
  const [stagingValue, setStagingValue] = React.useState<EntityTypePropertyValue | null>(null);

  const handleBaseEntityUpdated = (value: EntityTypePropertyValue) => {
    props.onChange({
      ...props.value,
      baseEntity: value.entityTypeId,
      baseEntityColumns: value.entityTypeProperties,
    });
  };

  const handleJoinedEntityUpdated = (joinedProperty: string, value: EntityTypePropertyValue) => {
    setJoinedProperty(joinedProperty);
    setStagingValue(value);
  };

  const handleJoinedEntityAdded = () => {
    props.onChange({
      ...props.value,
      joinedEntities: {
        ...props.value.joinedEntities,
        [joinedProperty]: {
          ...props.value.joinedEntities[joinedProperty],
          [stagingValue.entityTypeId]: stagingValue.entityTypeProperties,
        },
      },
    });

    setJoinedProperty(null);
    setStagingValue({
      entityTypeId: null,
      entityTypeProperties: [],
    });
  };

  const handleJoinedEntityDeleted = (joinedProperty: string, entityTypeId: string) => {
    const newJoinedEntities = { ...props.value.joinedEntities };
    delete newJoinedEntities[joinedProperty][entityTypeId];

    props.onChange({
      ...props.value,
      joinedEntities: newJoinedEntities,
    });
  };

  const handleUpdated = (name: string, value: string) => {
    props.onChange({
      ...props.value,
      [name]: value,
    });
  };

  const getSelectedColumns = () => {
    const properties = [];
    for (const column of props.value.baseEntityColumns || []) {
      properties.push(column);
    }

    for (const joinedProperty in props.value.joinedEntities) {
      for (const entityTypeId in props.value.joinedEntities[joinedProperty]) {
        for (const column of props.value.joinedEntities[joinedProperty][entityTypeId]) {
          properties.push(column);
        }
      }
    }

    return properties;
  };

  if (props.readonly) {
    return (
      <>
        <FormStyle.InputGroup>
          <h3>Selected properties</h3>
        </FormStyle.InputGroup>
        <EntityTypeSelectionPreview data={props.value ?? ({} as IMachineLearningExperiment)} />
      </>
    );
  }

  const selectedColumns = getSelectedColumns();

  return (
    <>
      <FormStyle.InputGroup>
        <h3>Select base entity type </h3>
      </FormStyle.InputGroup>

      <EntityTypePropertySelector
        disabled={props.disabled}
        entityTypes={props.entityTypes}
        value={{
          entityTypeId: props.value?.baseEntity,
          entityTypeProperties: props.value?.baseEntityColumns ?? [],
        }}
        onChange={handleBaseEntityUpdated}
      />

      <FormStyle.InputGroup>
        <h3>Joined entity types</h3>
      </FormStyle.InputGroup>

      {(props.value?.baseEntityColumns ?? []).map((joinedProperty) => (
        <FormStyle.InputGroup key={joinedProperty}>
          {Object.keys(props.value.joinedEntities[joinedProperty] ?? {}).map((entityTypeId) => (
            <JoinedEntityTypePropertySelector
              readonly
              key={entityTypeId}
              joinableProperties={props.value?.baseEntityColumns ?? []}
              entityTypes={props.entityTypes}
              value={{
                joinedProperty,
                entityTypeId,
                entityTypeProperties: props.value.joinedEntities[joinedProperty][entityTypeId],
              }}
              onDelete={handleJoinedEntityDeleted}
            />
          ))}
        </FormStyle.InputGroup>
      ))}

      <JoinedEntityTypePropertySelector
        key="new"
        disabled={props.disabled || (props.value?.baseEntityColumns || []).length === 0}
        joinableProperties={props.value?.baseEntityColumns ?? []}
        entityTypes={props.entityTypes}
        value={{
          joinedProperty,
          entityTypeId: stagingValue?.entityTypeId,
          entityTypeProperties: stagingValue?.entityTypeProperties ?? [],
        }}
        onChange={handleJoinedEntityUpdated}
        onDone={handleJoinedEntityAdded}
      />

      <FormStyle.InputGroup>
        <h3>Additional data configuration</h3>
      </FormStyle.InputGroup>

      <FormStyle.InputGroup>
        <Dropdown
          disabled={props.disabled}
          options={selectedColumns.map((p) => ({ key: p, text: p }))}
          label="Split column"
          selectedKey={props.value.splitColumn}
          placeholder="Select a column to split the data in highly skewed data sets (i.e. CountryCode)"
          onChange={(e, option) => handleUpdated('splitColumn', option.key.toString())}
        />
      </FormStyle.InputGroup>

      <FormStyle.InputGroup>
        <Dropdown
          disabled={props.disabled}
          options={selectedColumns.map((p) => ({ key: p, text: p }))}
          label="Sensitive properties"
          multiSelect
          selectedKeys={props.value.hiddenProperties || []}
          placeholder="Select columns that should be hidden from reports (i.e. PersonnelNumber, Alias)"
          onChange={(e, option) => {
            props.onChange({
              ...props.value,
              hiddenProperties: option.selected
                ? [...(props.value.hiddenProperties || []), option.key.toString()]
                : (props.value.hiddenProperties || []).filter((p) => p !== option.key.toString()),
            });
          }}
        />
      </FormStyle.InputGroup>
      {/* <Preview>
        <FormStyle.InputGroup>
          <h3>Selected properties</h3>
        </FormStyle.InputGroup>

        <EntityTypeSelectionPreview data={props.value ?? ({} as IMachineLearningExperiment)} />
      </Preview> */}
    </>
  );
}
